
import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, User } from "lucide-react";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { ProfileForm } from "@/components/profile/ProfileForm";
import { ReadOnlyProfile } from "@/components/profile/ReadOnlyProfile";
import { useProfileData } from "@/hooks/useProfileData";

interface UserProfileProps {
  userId?: string; // If not provided, uses the current user
  readOnly?: boolean;
}

export function UserProfile({ userId, readOnly = false }: UserProfileProps) {
  const { user } = useAuth();
  const { 
    profile, 
    loading, 
    role, 
    avatarUrl, 
    updateProfile, 
    handleAvatarUpdated, 
    getFormValues 
  } = useProfileData(userId);

  const profileId = userId || user?.id;

  if (loading && !profile) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <Card className="shadow-sm border-slate-200">
      <CardHeader className="pb-4">
        <CardTitle>User Profile</CardTitle>
        <CardDescription>
          {readOnly 
            ? "View user profile information" 
            : "Update your profile information and avatar"
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* User Avatar and Basic Info */}
        <ProfileHeader
          userId={profileId!}
          profile={profile}
          user={user}
          role={role}
          avatarUrl={avatarUrl}
          readOnly={readOnly}
          onAvatarUpdated={handleAvatarUpdated}
        />
        
        {/* Profile Form or Read-Only View */}
        {!readOnly ? (
          <ProfileForm
            defaultValues={getFormValues()}
            loading={loading}
            readOnly={readOnly}
            onSubmit={updateProfile}
          />
        ) : (
          <ReadOnlyProfile profile={profile} />
        )}
      </CardContent>
      
      {readOnly && (
        <CardFooter className="border-t bg-slate-50 flex justify-end">
          <Button variant="outline" asChild>
            <a href={`/dashboard/users/${profileId}`}>
              View Full Profile
            </a>
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}
